import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../data/DataProvider';
import { money, date, time, invoiceTotal } from '../shared/formatters';
import KeyDateList from '../shared/components/KeyDateList';
import SafeIcon from '../common/SafeIcon';
import { Icons } from '../shared/icons';

export default function Dashboard() {
  const { data } = useData();
  const navigate = useNavigate();
  const outstanding = data.invoices.filter((invoice) => invoice.status !== 'paid').reduce((sum, invoice) => sum + invoiceTotal(invoice), 0);
  const unread = data.messages.filter((message) => !message.read_at && message.sender === 'client').length;
  const upcoming = data.keyDates.filter((item) => new Date(item.date) >= new Date() && new Date(item.date) < new Date(Date.now() + 2592000000));
  const quotesAwaiting = data.quotes.filter((quote) => quote.status === 'sent').length;
  const certsExpiring = data.certificates.filter((cert) => cert.expiry_date && new Date(cert.expiry_date) - Date.now() < 30 * 86400000).length;

  // Portal activity (pass 7): newest first, grouped by client for a quick
  // "who logged in / what did they open" glance.
  const recentVisits = (data.portalVisits || []).slice(0, 8);
  const pathLabel = (path) => {
    const map = {
      '/': 'Home',
      '/invoices': 'Invoices',
      '/estimates': 'Estimates',
      '/quotes': 'Quotes',
      '/history': 'History',
      '/keydates': 'Key dates',
      '/documents': 'Documents',
      '/chat': 'Messages',
    };
    if (map[path]) return map[path];
    if (path.startsWith('/invoices/')) return 'Invoice';
    if (path.startsWith('/quotes/')) return 'Quote';
    if (path.startsWith('/history/')) return 'Job details';
    return path;
  };

  return (
    <div className="standalone-page">
      <header className="simple-header">
        <div>
          <span className="eyebrow">Agency workspace</span>
          <h1>Good morning, Simon</h1>
        </div>
        <div className="quick-actions">
          <button className="primary-button" onClick={() => navigate('/admin/clients')}>+ New client</button>
          <button className="secondary-button" onClick={() => navigate('/admin/clients')}>Log payment</button>
          <button className="secondary-button" onClick={() => navigate('/admin/quotes/new')}>New quote</button>
        </div>
      </header>
      <div className="stats-grid">
        <button className="stat-card" onClick={() => navigate('/admin/clients?filter=balance')}>
          <i><SafeIcon icon={Icons.CreditCard} /></i>
          <span>Outstanding</span>
          <strong>{money(outstanding)}</strong>
          <small>Across active accounts</small>
        </button>
        <button className="stat-card" onClick={() => navigate('/admin/messages')}>
          <i><SafeIcon icon={Icons.Inbox} /></i>
          <span>Unread messages</span>
          <strong>{unread}</strong>
          <small>Needs a reply</small>
        </button>
        <button className="stat-card" onClick={() => navigate('/admin/clients')}>
          <i><SafeIcon icon={Icons.Calendar} /></i>
          <span>Upcoming dates</span>
          <strong>{upcoming.length}</strong>
          <small>Next 30 days</small>
        </button>
        <button className="stat-card" onClick={() => navigate('/admin/quotes')}>
          <i><SafeIcon icon={Icons.FileText} /></i>
          <span>Quotes awaiting approval</span>
          <strong>{quotesAwaiting}</strong>
          <small>Sent, awaiting the client</small>
        </button>
        <button className="stat-card" onClick={() => navigate('/admin/certificates')}>
          <i><SafeIcon icon={Icons.Bell} /></i>
          <span>Certificates expiring soon</span>
          <strong>{certsExpiring}</strong>
          <small>Within 30 days</small>
        </button>
      </div>
      <div className="dashboard-columns">
        <section className="panel">
          <div className="panel-head"><h3>Upcoming key dates</h3></div>
          <KeyDateList dates={upcoming} />
        </section>
        <section className="panel">
          <div className="panel-head"><h3>Recent activity</h3></div>
          <div className="activity-list">
            {data.messages.slice(-6).reverse().map((message) => (
              <button key={message.id} onClick={() => navigate(`/admin/clients/${message.client_id}`)}>
                <i><SafeIcon icon={Icons.MessageCircle} /></i>
                <p>
                  <strong>{message.name} · {data.clients.find((client) => client.id === message.client_id)?.name}</strong>
                  <span>{message.body}</span>
                </p>
                <time>{date(message.created_at)}</time>
              </button>
            ))}
          </div>
        </section>
        <section className="panel">
          <div className="panel-head"><h3>Portal activity</h3></div>
          <div className="activity-list">
            {recentVisits.map((visit) => (
              <button key={visit.id} onClick={() => navigate(`/admin/clients/${visit.client_id}`)}>
                <i><SafeIcon icon={Icons.Activity} /></i>
                <p>
                  <strong>{data.clients.find((client) => client.id === visit.client_id)?.name || 'Client'}</strong>
                  <span>Opened {pathLabel(visit.path)}</span>
                </p>
                <time>{date(visit.visited_at)}, {time(visit.visited_at)}</time>
              </button>
            ))}
            {recentVisits.length === 0 && <p className="field-hint">No portal visits yet — visit times appear here once clients log in.</p>}
          </div>
        </section>
      </div>
    </div>
  );
}
